import { useMemo, useState } from 'react';
import { categories, collections } from '@/mocks/home';
import CollectionModal from './CollectionModal';

const filters = [{ id: 'all', name: '全部合集' }, ...categories.map((c) => ({ id: c.id, name: c.name }))];

interface CollectionsProps {
  activeFilter: string;
  onFilterChange: (filterId: string) => void;
}

export default function Collections({ activeFilter, onFilterChange }: CollectionsProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const visible = useMemo(
    () => (activeFilter === 'all' ? collections : collections.filter((c) => c.categoryId === activeFilter)),
    [activeFilter],
  );

  const selected = collections.find((c) => c.id === selectedId) || null;
  const selectedCategoryName =
    categories.find((c) => c.id === selected?.categoryId)?.name || '影像合集';

  return (
    <section id="collections" className="w-full bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-sm font-label font-medium text-primary-700">
              <span className="w-6 h-[2px] bg-primary-500" aria-hidden="true"></span>
              热门合集
            </span>
            <h2 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-snug">
              最近被反复回看的影像合集
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              点击任意卡片可查看该合集的素材构成、时长与入库时间。合集按更新顺序排列，最新的排在最前。
            </p>
          </div>
          <p className="text-sm text-foreground-500 font-label whitespace-nowrap">
            共 <span className="font-heading font-bold text-lg text-accent-600">{visible.length}</span> 个合集
          </p>
        </div>

        <div className="mt-8 md:mt-10 flex flex-wrap gap-2">
          {filters.map((f) => {
            const isActive = activeFilter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => onFilterChange(f.id)}
                className={`px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer border ${
                  isActive
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-100 text-foreground-600 border-background-200 hover:border-primary-300 hover:text-foreground-950'
                }`}
                aria-pressed={isActive}
              >
                {f.name}
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {visible.map((item) => {
            const catName = categories.find((c) => c.id === item.categoryId)?.name || '';
            return (
              <article
                key={item.id}
                className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(item.id)}
                  className="relative w-full aspect-[8/5] overflow-hidden cursor-pointer text-left"
                  aria-label={`查看合集：${item.title}`}
                >
                  <img
                    src={item.cover}
                    alt={`${item.title} 封面预览`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-foreground-950/65 text-background-50 text-[11px] font-label backdrop-blur-sm">
                    {catName}
                  </span>
                  {item.duration !== '00:00' && (
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-foreground-950/70 text-background-50 text-[11px] font-label">
                      <i className="ri-play-fill" aria-hidden="true"></i>
                      {item.duration}
                    </span>
                  )}
                  <span className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/25 transition-colors flex items-center justify-center">
                    <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/0 group-hover:bg-background-50/90 text-transparent group-hover:text-primary-600 transition-all">
                      <i className="ri-play-circle-line text-3xl" aria-hidden="true"></i>
                    </span>
                  </span>
                </button>

                <div className="flex flex-col flex-1 p-4 md:p-5">
                  <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 leading-snug">
                    {item.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600 line-clamp-2">
                    {item.summary}
                  </p>
                  <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500 font-label">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {item.views}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-calendar-line" aria-hidden="true"></i>
                      {item.date}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <CollectionModal
        item={selected}
        categoryName={selectedCategoryName}
        onClose={() => setSelectedId(null)}
      />
    </section>
  );
}